/* LITE COCKPIT — the whole app for a plant that joined through its pump company's invite.
   Logo, who they're connected to, their trucks, today's jobs, one SEND TRUCK button. Nothing else. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Truck, Plus, Loader2, LogOut, ArrowUpRight, Phone, MapPin, Check, Undo2, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const field = "w-full bg-[#0B0D10] border border-[#2C313A] focus:border-primary-orange outline-none text-white px-3 py-3 text-base";
const STATUS_LABEL = { available: "In yard", en_route: "On the way", on_site: "On site", discharging: "Pouring", returning: "Heading back", loading: "Loading" };

export default function LiteCockpit() {
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ name: "", driver_name: "", driver_phone: "" });
  const [busy, setBusy] = useState("");
  const [picking, setPicking] = useState(null);   // job id awaiting truck choice

  const load = useCallback(async () => {
    try { const r = await api.get("/lite/overview"); setData(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load today"); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const addTruck = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Give the truck a name");
    setBusy("truck");
    try {
      await api.post("/lite/trucks", f);
      setF({ name: "", driver_name: "", driver_phone: "" }); setAdding(false);
      toast.success("Truck added"); load();
    } catch (ex) { toast.error(ex.response?.data?.detail || "Could not add truck"); }
    finally { setBusy(""); }
  };

  const removeTruck = async (t) => {
    if (!window.confirm(`Remove ${t.name}?`)) return;
    try { await api.delete(`/lite/trucks/${t.id}`); load(); }
    catch (ex) { toast.error(ex.response?.data?.detail || "Could not remove"); }
  };

  const send = async (jobId, truckId) => {
    setBusy(jobId); setPicking(null);
    try {
      const r = await api.post(`/lite/jobs/${jobId}/send-truck`, { truck_id: truckId || null });
      toast.success(r.data.sms_sent
        ? `${r.data.truck_name} sent — driver texted the tracking link`
        : `${r.data.truck_name} sent${r.data.driver_phone ? "" : " — add the driver's mobile to text him the tracker"}`);
      load();
    } catch (ex) { toast.error(ex.response?.data?.detail || "Could not send the truck"); }
    finally { setBusy(""); }
  };

  const sendTap = (job) => {
    const trucks = data?.trucks || [];
    if (trucks.length === 0) return toast.error("Add a truck first");
    if (trucks.length === 1) return send(job.id, trucks[0].id);
    setPicking(picking === job.id ? null : job.id);
  };

  const recall = async (jobId, truckId) => {
    try { await api.post(`/lite/jobs/${jobId}/recall/${truckId}`); load(); }
    catch (ex) { toast.error(ex.response?.data?.detail || "Could not recall"); }
  };

  if (!data) return (
    <div className="min-h-screen bg-[#0B0D10] flex items-center justify-center">
      <Loader2 className="w-6 h-6 text-primary-orange animate-spin" />
    </div>
  );

  const pumpNames = (data.pump_companies || []).map((p) => p.name).filter(Boolean);

  return (
    <div className="min-h-screen bg-[#0B0D10] text-white pb-16" data-testid="lite-cockpit">
      <header className="border-b border-[#22262E] px-4 py-5 sm:px-8">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="w-10 h-10 bg-primary-orange flex items-center justify-center shrink-0">
            <Truck className="w-5 h-5 text-black" />
          </div>
          <div className="min-w-0">
            <div className="text-[9px] tracking-[0.3em] text-[#8B909A]">ARMORBATCH GLOBAL</div>
            <div className="heading text-white text-lg sm:text-xl font-black uppercase truncate" data-testid="lite-org-name">{data.org.name}</div>
          </div>
          <button onClick={logout} data-testid="lite-logout" className="ml-auto p-2 border border-[#2C313A] text-[#8B909A] hover:text-white"><LogOut className="w-4 h-4" /></button>
        </div>
        {pumpNames.length > 0 && (
          <div className="max-w-3xl mx-auto mt-3 text-[13px] text-[#A1A6B0]" data-testid="lite-connected">
            Connected to <span className="text-primary-orange font-bold">{pumpNames.join(", ")}</span>
          </div>
        )}
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-6 space-y-8">
        {/* ── Trucks ── */}
        <section data-testid="lite-trucks">
          <div className="flex items-center justify-between gap-3">
            <h2 className="heading text-white text-base md:text-lg font-black uppercase tracking-wide">My Trucks</h2>
            <button onClick={() => setAdding(!adding)} data-testid="lite-add-truck-btn"
              className="bg-primary-orange text-black font-black uppercase tracking-widest text-xs px-3 py-2 inline-flex items-center gap-1"><Plus className="w-4 h-4" /> Add truck</button>
          </div>

          {adding && (
            <form onSubmit={addTruck} className="mt-3 space-y-2 border border-primary-orange/50 p-4" data-testid="lite-truck-form">
              <input data-testid="lite-truck-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Truck name or rego" className={field} />
              <input data-testid="lite-truck-driver" value={f.driver_name} onChange={(e) => setF({ ...f, driver_name: e.target.value })} placeholder="Driver name" className={field} />
              <input data-testid="lite-truck-phone" value={f.driver_phone} onChange={(e) => setF({ ...f, driver_phone: e.target.value })} placeholder="Driver mobile (so we can text him the tracker)" className={field} />
              <button type="submit" disabled={busy === "truck"} data-testid="lite-truck-save"
                className="w-full bg-primary-orange text-black font-black uppercase tracking-widest text-sm py-3 disabled:opacity-50">{busy === "truck" ? "Saving…" : "Save truck"}</button>
            </form>
          )}

          <div className="mt-3 space-y-2">
            {(data.trucks || []).length === 0 && !adding && (
              <div className="border border-dashed border-[#2C313A] p-6 text-center text-[13px] text-[#A1A6B0]" data-testid="lite-trucks-empty">
                Add your trucks and drivers to start sending them to jobs.
              </div>
            )}
            {(data.trucks || []).map((t) => (
              <div key={t.id} className="border border-[#22262E] bg-[#12151A] px-4 py-3 flex items-center gap-3" data-testid={`lite-truck-${t.id}`}>
                <Truck className="w-4 h-4 text-primary-orange shrink-0" />
                <div className="min-w-0">
                  <div className="font-black uppercase truncate">{t.name}</div>
                  <div className="mono text-[11px] text-[#A1A6B0] truncate">{t.driver_name || "driver TBA"}{t.driver_phone ? ` · ${t.driver_phone}` : " · no mobile"}</div>
                </div>
                <div className="ml-auto flex items-center gap-2 shrink-0">
                  <span className="mono text-[10px] uppercase tracking-widest text-[#8B909A]">{STATUS_LABEL[t.status] || t.status}</span>
                  <button onClick={() => removeTruck(t)} data-testid={`lite-truck-del-${t.id}`} className="p-2 border border-[#2C313A] text-[#8B909A] hover:border-red-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Today's jobs ── */}
        <section data-testid="lite-jobs">
          <h2 className="heading text-white text-base md:text-lg font-black uppercase tracking-wide">Today's Jobs</h2>
          {(data.jobs || []).length === 0 ? (
            <div className="mt-3 border border-dashed border-[#2C313A] p-8 text-center" data-testid="lite-jobs-empty">
              <MapPin className="w-7 h-7 mx-auto text-primary-orange" />
              <div className="font-black uppercase mt-2">No jobs yet today</div>
              <p className="text-[13px] text-[#A1A6B0] mt-1">
                {pumpNames.length ? `${pumpNames[0]} raises the jobs — they'll show up here the moment they do.` : "Your pump company's jobs will show up here."}
              </p>
            </div>
          ) : (
            <div className="mt-3 space-y-3">
              {(data.jobs || []).map((j) => (
                <div key={j.id} className={`border bg-[#12151A] p-4 ${j.rolling ? "border-primary-orange" : "border-[#22262E]"}`} data-testid={`lite-job-${j.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="mono text-lg font-black text-primary-orange shrink-0">{j.time_required || "--:--"}</div>
                    <div className="min-w-0">
                      <div className="font-black uppercase truncate">{j.site_name}</div>
                      <div className="text-[13px] text-[#A1A6B0] truncate">{j.site_address}</div>
                      <div className="mono text-[11px] text-[#8B909A] mt-1">
                        {j.quantity_m3 ? `${j.quantity_m3} m³` : ""}{j.mix_code ? ` · ${j.mix_code}` : ""}{j.pump_company ? ` · ${j.pump_company}` : ""}
                      </div>
                      {j.notes && <div className="text-[12px] text-[#A1A6B0] mt-1">{j.notes}</div>}
                    </div>
                    {j.pump_phone && (
                      <a href={`tel:${j.pump_phone}`} className="ml-auto p-2 border border-[#2C313A] text-[#A1A6B0] hover:text-white shrink-0" data-testid={`lite-call-pump-${j.id}`}><Phone className="w-4 h-4" /></a>
                    )}
                  </div>

                  {j.trucks.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {j.trucks.map((t) => (
                        <div key={t.id} className="flex items-center gap-2 mono text-[11px] uppercase tracking-widest" data-testid={`lite-job-truck-${j.id}-${t.id}`}>
                          <Check className="w-3.5 h-3.5 text-green-400" />
                          <span className="text-white">{t.name}</span>
                          <span className="text-[#8B909A]">{STATUS_LABEL[t.status] || t.status}</span>
                          <button onClick={() => recall(j.id, t.id)} title="Recall" data-testid={`lite-recall-${j.id}-${t.id}`} className="ml-auto text-[#8B909A] hover:text-red-400"><Undo2 className="w-3.5 h-3.5" /></button>
                        </div>
                      ))}
                    </div>
                  )}

                  <button onClick={() => sendTap(j)} disabled={busy === j.id} data-testid={`lite-send-truck-${j.id}`}
                    className="mt-3 w-full bg-primary-orange text-black font-black uppercase tracking-widest text-sm py-3.5 disabled:opacity-50 inline-flex items-center justify-center gap-2">
                    {busy === j.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
                    {j.trucks.length ? "Send another truck" : "Send truck"}
                  </button>

                  {picking === j.id && (
                    <div className="mt-2 border border-primary-orange divide-y divide-[#22262E]" data-testid={`lite-truck-picker-${j.id}`}>
                      {(data.trucks || []).map((t) => (
                        <button key={t.id} onClick={() => send(j.id, t.id)} data-testid={`lite-pick-truck-${j.id}-${t.id}`}
                          className="w-full text-left px-4 py-3 hover:bg-[#1A1E25] flex items-center gap-2">
                          <Truck className="w-4 h-4 text-primary-orange" />
                          <span className="font-black uppercase">{t.name}</span>
                          <span className="mono text-[11px] text-[#8B909A] ml-auto">{STATUS_LABEL[t.status] || t.status}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="pt-2 text-center">
          <a href="mailto:sales@armorbatch.com?subject=Upgrade%20to%20full%20dispatch" data-testid="lite-upgrade"
            className="mono text-[11px] uppercase tracking-widest text-[#8B909A] hover:text-primary-orange inline-flex items-center gap-1">
            Upgrade to full dispatch <ArrowUpRight className="w-3 h-3" />
          </a>
        </div>
      </main>
    </div>
  );
}
